"use client";

import { createContext, useContext, useEffect, useRef, useState } from "react";
import { reloadForMaintenance, reloadForUpdate } from "@/utils/version";

type Options = {
  intervalMs?: number;
  suppressWhenHidden?: boolean;
  onMaintenance?: () => void;
};

type VersionContextValue = {
  updateAvailable: boolean;
  maintenanceActive: boolean;
  latestVersion: string | null;
  latestBranch: string | null;
  dismiss: () => void;
  reloadNow: () => void;
};

const VersionCheckContext = createContext<VersionContextValue | undefined>(undefined);

const setIfChanged = <T,>(ref: { current: T }, setState: (v: T) => void, next: T) => {
  if (ref.current !== next) {
    ref.current = next;
    setState(next);
  }
};

export function VersionPollingProvider({
  children,
  intervalMs = 60000,
  suppressWhenHidden = true,
  onMaintenance = reloadForMaintenance,
}: { children: React.ReactNode } & Options) {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [latestVersion, setLatestVersion] = useState<string | null>(null);
  const [latestBranch, setLatestBranch] = useState<string | null>(null);
  const [maintenanceActive, setMaintenanceActive] = useState(false);

  const pollTimeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const updateDismissedRef = useRef(false);
  const requestSequenceRef = useRef(0);
  const initialVersionRef = useRef<string | null>(null);
  const latestVersionRef = useRef<string | null>(null);
  const latestBranchRef = useRef<string | null>(null);
  const updateAvailableRef = useRef(false);
  const maintenanceActiveRef = useRef(false);

  type VersionResponse = { version?: string; branch?: string; key?: string; maintenance?: boolean };

  useEffect(() => {
    let isCancelled = false;

    const fetchLatest = async () => {
      if (isCancelled) {
        return;
      }
      if (typeof globalThis.fetch !== "function") {
        return;
      }
      requestSequenceRef.current += 1;
      const requestId = requestSequenceRef.current;
      const request = (globalThis.fetch as typeof fetch)("/api/version", { cache: "no-store" });
      const response = (await Promise.resolve(request).catch(() => undefined)) as
        | Response
        | undefined;
      if (!response || !response.ok) {
        return;
      }
      const data = (await response.json()) as VersionResponse;
      if (isCancelled || requestId !== requestSequenceRef.current) {
        return;
      }
      const isMaintenance = data.maintenance === true;
      const enteredMaintenance = isMaintenance && !maintenanceActiveRef.current;
      setIfChanged(maintenanceActiveRef, setMaintenanceActive, isMaintenance);
      if (enteredMaintenance) {
        onMaintenance();
      }
      if (isMaintenance) {
        return;
      }
      const remoteVersion = data.key || data.version || null;
      const remoteBranch = data.branch || null;
      if (!remoteVersion) {
        return;
      }
      if (initialVersionRef.current == null) {
        initialVersionRef.current = remoteVersion;
        setIfChanged(latestVersionRef, setLatestVersion, remoteVersion);
        setIfChanged(latestBranchRef, setLatestBranch, remoteBranch);
        return;
      }
      const hasNewVersion = remoteVersion !== initialVersionRef.current;
      if (hasNewVersion && !updateDismissedRef.current) {
        setIfChanged(updateAvailableRef, setUpdateAvailable, true);
      }
      setIfChanged(latestVersionRef, setLatestVersion, remoteVersion);
      setIfChanged(latestBranchRef, setLatestBranch, remoteBranch);
    };

    const shouldPoll = () => !suppressWhenHidden || document.visibilityState === "visible";

    const poll = async () => {
      if (isCancelled) {
        return;
      }
      if (shouldPoll()) {
        await fetchLatest();
        if (isCancelled) {
          return;
        }
      }
      pollTimeoutIdRef.current = setTimeout(poll, intervalMs);
    };

    function handleVisibilityChange(): void {
      if (document.visibilityState !== "visible") {
        return;
      }
      void fetchLatest();
    }

    poll();
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      isCancelled = true;
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (pollTimeoutIdRef.current) {
        clearTimeout(pollTimeoutIdRef.current);
      }
    };
  }, [intervalMs, suppressWhenHidden, onMaintenance]);

  const dismiss = () => {
    updateDismissedRef.current = true;
    setIfChanged(updateAvailableRef, setUpdateAvailable, false);
  };

  function reloadNow(): void {
    reloadForUpdate();
  }

  const value: VersionContextValue = {
    updateAvailable,
    maintenanceActive,
    latestVersion,
    latestBranch,
    dismiss,
    reloadNow,
  };

  return <VersionCheckContext.Provider value={value}>{children}</VersionCheckContext.Provider>;
}

export function useVersionCheckContext() {
  const ctx = useContext(VersionCheckContext);
  if (!ctx) {
    throw new Error("useVersionCheckContext must be used within VersionPollingProvider");
  }
  return ctx;
}
